// src/pages/OperationsPage.jsx

import React from "react";
import { useNavigate } from "react-router-dom";

const hero = "/assets/resources/hero.jpg";
const nims = "/assets/resources/nims.jpg";
const briefing = "/assets/resources/briefing.jpg";
const triage = "/assets/resources/triage.jpg";
const bike = "/assets/resources/bike.jpg";
const hq = "/assets/resources/hq.jpg";
const stretcher = "/assets/resources/stretcher.jpg";
const inventory = "/assets/resources/inventory.jpg";
const cart = "/assets/resources/cart.jpg";
const birdie = "/assets/resources/birdie.jpg";
const supervisor = "/assets/resources/supervisor.jpg";
const transport = "/assets/resources/transport.jpg";
const mealbreak = "/assets/resources/mealbreak.jpg";
const dispatch = "/assets/resources/dispatch.jpg";
const camera = "/assets/resources/camera.jpg";
const tent = "/assets/resources/tent.jpg";

function Section({ title, text, image, reverse = false }) {
  return (
    <section style={{ padding: "32px 0" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: window.innerWidth >= 900 ? "1fr 1fr" : "1fr",
          gap: "24px",
          alignItems: "center",
        }}
      >
        <div style={{ order: reverse && window.innerWidth >= 900 ? 2 : 1 }}>
          <div
            style={{
              width: "100%",
              height: window.innerWidth >= 900 ? "420px" : "260px",
              overflow: "hidden",
              borderRadius: "16px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              background: "#f3f4f6",
            }}
          >
            <img
              src={image}
              alt={title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
                backgroundColor: "#ffffff",
              }}
            />
          </div>
        </div>

        <div style={{ order: reverse && window.innerWidth >= 900 ? 1 : 2 }}>
          <h2
            style={{
              fontSize: "36px",
              fontWeight: 300,
              color: "#7c3a8f",
              marginBottom: "16px",
            }}
          >
            {title}
          </h2>

          <div style={{ color: "#1f2937", fontSize: "18px", lineHeight: 1.7 }}>
            {text.map((t, i) => (
              <p key={i} style={{ marginBottom: "14px" }}>
                {t}
              </p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function OperationsPage() {
  const navigate = useNavigate();

  return (
  <div
    style={{
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "24px 16px 48px",
      minHeight: "100vh",
      overflowY: "auto",
    }}
  >
    <button
      onClick={() => navigate("/dashboard?quicklinks=true")}
      style={{
        marginBottom: 20,
        padding: "10px 16px",
        backgroundColor: "#6b4fa3",
        color: "white",
        border: "none",
        borderRadius: 6,
        cursor: "pointer",
        fontWeight: 500,
      }}
    >
      ← Return to Quick Links
    </button>

      <div style={{ marginBottom: "40px" }}>
        <div
          style={{
            width: "100%",
            height: window.innerWidth >= 900 ? "320px" : "220px",
            overflow: "hidden",
            borderRadius: "24px",
            boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
          }}
        >
          <img
            src={hero}
            alt="Travelers Championship"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </div>

        <div style={{ marginTop: "18px" }}>
          <h1 style={{ fontSize: "40px", fontWeight: 700, margin: 0 }}>
            Operations
          </h1>
        </div>
      </div>

      <Section
        title="NIMS"
        image={nims}
        text={[
          "The Travelers Championship utilizes the National Incident Management System to establish a chain of command, provide a manageable span of control, and clearly delineate roles and responsibilities to ensure an efficient and effective response to this large-scale, multi-day event.",
          "All medical team members are required to have a minimum of NIMS 100 prior to working at this event.",
        ]}
      />

      <Section
        title="Morning Briefing"
        image={briefing}
        reverse
        text={[
          "Each morning at 0600, our team will meet for a morning briefing. This is where introductions are made, general operations are discussed, and any pertinent information about the day's events is shared.",
          "A light grab-and-go breakfast and coffee will also be available each morning.",
        ]}
      />

      <Section
        title="Inventories"
        image={inventory}
        text={[
          "Conducting a thorough inventory of your assigned equipment is vital to our mission. Please be sure to familiarize yourself with your gear.",
          "Please do not move items around in the bags. Report any discrepancies to our Logistics Officer for replacement.",
        ]}
      />

      <Section
        title="Dispatch"
        image={dispatch}
        reverse
        text={[
          "We operate the same way any EMS agency would. We have areas and districts that each team is assigned to.",
          "All teams are tracked by GPS and are dispatched to calls for service based on their proximity to the call.",
          "Please do not jump calls. If you feel you are closer, please let the dispatcher know.",
        ]}
      />

      <Section
        title="Triage"
        image={triage}
        text={[
          "As with any hospital emergency room, we utilize a Triage Officer to direct patient flow at Medical HQ.",
          "When delivering patients to Medical HQ, report to the Triage Officer so they can direct you to your patient's assigned bed.",
          "This process ensures that all patients receive appropriate care in a timely manner.",
        ]}
      />

      <Section
        title="Medical HQ"
        image={hq}
        reverse
        text={[
          "The Medical HQ tent is equipped to handle several patients. This is a clinical area and should be treated as such.",
          "Team members not directly involved in patient care should avoid this area when patients are present.",
          "This tent is staffed with physicians, nurses, APRNs, PAs, paramedics, and EMTs.",
        ]}
      />

      <Section
        title="Tent Teams"
        image={tent}
        text={[
          "We operate five forward medical tents on the course. Tents are staffed with EMTs and paramedics.",
          "While working in a tent, your primary mission is to interact with the public. Each tent acts as a water station and is stocked with items like sunscreen.",
          "Tents are equipped with BLS or ALS gear depending on the staffing level. Tents are not the place to put your feet up, lay on the cot or stare at your phone all day.",
          "You will be very visible to the public and are expected to maintain a professional appearance at all times. Please remember to keep your voices low, as the tent can amplify sound and cause your voice to travel out onto the course.",
          "Each tent has an assigned ALS cart team.",
        ]}
      />

      <Section
        title="Bike Teams"
        image={bike}
        reverse
        text={[
          "We deploy three ALS bike teams during the tournament. Each team consists of one EMT and one paramedic.",
          "Bike teams are a great way to interact with the public and access hard-to-reach places. Bike teams are stationed around the course and respond to calls in their assigned response area.",
          "IPMBA certification is preferred but not required. If you are assigned to a bike team, please use extreme caution when riding through crowds and always wear a helmet.",
          "Feel free to bring your own helmet. If you do not have one, we will provide one.",
        ]}
      />

      <Section
        title="Cart Teams"
        image={cart}
        text={[
          "We operate six ALS cart teams. Each cart team consists of one EMT and one paramedic and carries a limited supply of basic ALS gear.",
          "Cart teams are located at each of the five tents, as well as Cart 15, which is posted in the area of the 15th hole.",
          "All team members assigned to a cart team must watch the PGA's Golf Cart Safety Video and sign the acknowledgment form found in the golf cart safety section.",
        ]}
      />

      <Section
        title="Operations Supervisors"
        image={supervisor}
        reverse
        text={[
          "There are two supervisors on the course at all times. Ops 1 oversees the front 9, and Ops 2 oversees the back 9.",
          "Both supervisors carry a full set of ALS gear and will respond to calls as needed.",
        ]}
      />

      <Section
        title="Stretcher Carts"
        image={stretcher}
        text={[
          "We staff two stretcher carts: one MD/stretcher cart staffed by one of our physicians and based at Medical HQ, and a second stretcher cart based at Tent 3 and staffed by Tent 3 personnel.",
        ]}
      />

      <Section
        title="Transport"
        image={transport}
        reverse
        text={[
          "We operate a six-seat transport cart as a courtesy transport shuttle service for spectators with mobility issues to and from the entrance gates.",
          "This is a great opportunity for our team members to interact with the public and leave them with a positive impression of our service and the tournament.",
          "Staff assigned to this position need to have a thorough understanding of the course layout and must enjoy interacting with the public.",
        ]}
      />

      <Section
        title="Responding to Emergencies"
        image={hero}
        text={[
          "There are many challenges to overcome when responding to emergencies on the course. Please take the time to familiarize yourself with routes and access points in your assigned area.",
          "You are not permitted to go inside the ropes or into restricted areas unless you are responding to an emergency. Every effort should be made to avoid interruptions to play.",
          "Crossing fairways should be a last resort and should only be done once waived through by a player, caddie, or marshal.",
          "If the call is serious, we will have multiple teams responding from multiple directions. There will rarely be a reason to cross a fairway during play.",
          "If you have questions regarding response routes, please ask the Ops Supervisor in your area.",
        ]}
      />

      <Section
        title="Meal Breaks"
        image={mealbreak}
        reverse
        text={[
          "All team members will be given meal vouchers to use as they please. If you cannot get food in your assigned area, you will need to contact your area's Ops Supervisor for permission to leave your area.",
          "You will be given ample opportunities for breaks, but we ask that you take them in a discreet location or in an approved area.",
          "Approved areas for meal breaks include the Fan Zone and the staff lounge at Medical HQ. Food is not permitted in patient treatment areas.",
        ]}
      />

      <Section
        title="Patient Privacy"
        image={camera}
        text={[
          "Patient privacy is our top priority. There are cameras everywhere on the course.",
          "Patients should be removed from the course and brought to Medical HQ as soon as it is safe to do so.",
          "This not only limits interruptions to play, but more importantly, limits the patient's exposure to the public.",
        ]}
      />

      <Section
        title="TEXT BIRDIE"
        image={birdie}
        reverse
        text={[
          "Tournament Operations has a notification system in place to report anything that needs attention during the tournament.",
          "This may include an unsafe condition, requests for water station refills, or anything else you feel is important.",
          "Our role at the tournament is not only to treat patients, but also to identify potentially unsafe conditions and report them.",
          "To report an unsafe situation or make a request, text the word BIRDIE followed by your message to 860-864-4357.",
        ]}
      />
    </div>
  );
}